import {
  type Branded,
  type NodeStyle,
  type NodeType,
  brand,
  nodeTypeIcons,
} from '@ipfabric/utils';
import { Texture } from 'pixi.js';

interface NodeIconCacheItem {
  svg: string;
  url?: string;
  texture?: Texture;
}

type NodeIconCacheKey = Branded<string, 'NodeIconCacheKey'>;

type GraphNodeIconCache = Record<NodeIconCacheKey, NodeIconCacheItem>;

/**
 * We upscale each icon for PIXI because if we provide a 40x40 svg to PIXI, it generates
 * 40x40 bitmap texture that looks awful when zoomed
 */
const NODE_ICON_SCALE = 10;

const iconCache: GraphNodeIconCache = {};

const getNodeIconCacheKey = (
  nodeType: NodeType,
  style: NodeStyle,
): NodeIconCacheKey =>
  brand<NodeIconCacheKey>(`${nodeType}/${style.background}/${style.outline}`);

const createSvgDataUrl = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const getNodeIconCacheItem = (
  nodeType: NodeType,
  style: NodeStyle,
): NodeIconCacheItem => {
  const key = getNodeIconCacheKey(nodeType, style);
  if (!iconCache[key]) {
    iconCache[key] = {
      svg: nodeTypeIcons[nodeType].createIconSvg({
        scale: NODE_ICON_SCALE,
        style,
      }),
    };
  }
  // Fixed noUncheckedIndexedAccess NIM-19408
  return iconCache[key]!;
};

export const getGraphNodeIconTexture = (
  nodeType: NodeType,
  style: NodeStyle,
): Texture => {
  const cacheItem = getNodeIconCacheItem(nodeType, style);
  if (!cacheItem.texture) {
    cacheItem.texture = Texture.from(cacheItem.svg);
  }
  return cacheItem.texture;
};

export const getGraphNodeIconUrl = (
  nodeType: NodeType,
  style: NodeStyle,
): string => {
  const cacheItem = getNodeIconCacheItem(nodeType, style);
  if (!cacheItem.url) {
    cacheItem.url = createSvgDataUrl(cacheItem.svg);
  }
  return cacheItem.url;
};
